// Related-table value bindings for datasheet templates.
//
// A value box binds "Table.Field". Tags and Index_Sheet resolve against the tag and index sheet
// rows directly; any OTHER table resolves when the tag or the index sheet points at it through a
// foreign key recorded in AppCfg_FieldMetadata (FK_Table). ISA_Type.ISA_Desc, for example,
// follows Tags.ISA_Type to its ISA_Type row and prints ISA_Desc from there. One hop only: a
// related row's own FKs print their {Field}_Display text, but are not followed further.
//
// Mirrored on the server by DsSnapshotBuilder.FindRelatedLink / BoundTables and the related
// branch of ResolveLine (PMD-API/PMD.API/Services/DsSnapshotBuilder.cs). Change both.

const DIRECT_TABLES = new Set(['tags', 'index_sheet']);

// Case-insensitive read of one field, '' when absent or null. The dynamic API camel-cases
// column names (iSA_Desc), while bindings keep whatever case the author typed.
export function readField(rec, name) {
  if (!rec || !name) return '';
  const lower = String(name).toLowerCase();
  const key = Object.keys(rec).find(k => k.toLowerCase() === lower);
  return key && rec[key] != null ? String(rec[key]) : '';
}

// What a bound field prints: its {Field}_Display companion when the row carries one (an FK
// column holds an ID), else the raw value. Mirrors DsSnapshotBuilder.FieldText.
export function fieldText(rec, field) {
  return rec ? (readField(rec, `${field}_display`) || readField(rec, field)) : '';
}

// AppCfg_FieldMetadata rows (as /api/appcfg/fieldmetadata/{table} returns them) reduced to the
// FK fields, in metadata order: Display_Order, then the row ID.
export function toFkFields(metaRows = []) {
  return metaRows
    .filter(m => m && m.fK_Table)
    .map(m => ({
      table: m.table_Name,
      field: m.field_Name,
      fkTable: m.fK_Table,
      adminDb: m.fK_Admin_DB === true,
      order: m.display_Order ?? Number.MAX_SAFE_INTEGER,
      id: m.id ?? 0,
    }))
    .sort((a, b) => (a.order - b.order) || (a.id - b.id));
}

// The FK field that reaches `table`: a Tags field whose FK_Table is that table, else an
// Index_Sheet field. When several Tags fields point at the same table, the first in metadata
// order wins - a binding names only the table, so it cannot say which of them it meant.
// Null for Tags / Index_Sheet themselves and for a table nothing points at.
export function relatedLinkFor(table, fkFields = []) {
  const t = String(table || '').toLowerCase();
  if (!t || DIRECT_TABLES.has(t)) return null;
  const sorted = [...fkFields].sort((a, b) => (a.order - b.order) || (a.id - b.id));
  const on = (owner) => sorted.find(f =>
    String(f.table).toLowerCase() === owner && String(f.fkTable).toLowerCase() === t);
  const hit = on('tags') || on('index_sheet');
  return hit
    ? { table: hit.fkTable, owner: String(hit.table).toLowerCase(), fkField: hit.field, adminDb: hit.adminDb }
    : null;
}

// Every table other than Tags / Index_Sheet that a value box binds, on either side, lower-cased
// and distinct. Boxes are linesToBoxes output (fieldTable / fieldTableIdx already split).
export function boundTables(boxes = []) {
  const out = new Set();
  for (const b of boxes) {
    if (b.type !== 'value') continue;
    for (const t of [b.fieldTable, b.fieldTableIdx]) {
      const lower = String(t || '').toLowerCase();
      if (lower && !DIRECT_TABLES.has(lower)) out.add(lower);
    }
  }
  return [...out];
}

// The FK value (the related row's ID) a link reads from the tag or index sheet row.
export function relatedId(link, tagRow, indexSheetRow) {
  const src = link.owner === 'tags' ? tagRow : indexSheetRow;
  const raw = readField(src, link.fkField);
  const id = Number.parseInt(raw, 10);
  return Number.isFinite(id) ? id : null;
}
